import React from "react";
import { motion } from "framer-motion";
import {
  HeartPulse,
  Users,
  ShieldCheck,
  Lightbulb,
  Stethoscope,
  Video,
  FileText,
  Pill,
  CalendarCheck,
  ArrowRight,
  Check,
  Globe2,
  Target,
  Sparkles,
  Building2,
  Activity,
  ClipboardCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../../../DashBoardComponents/Navbar";

const AboutDoxzon = () => {
  const services = [
    {
      icon: Stethoscope,
      title: "Healthcare Professionals",
      description:
        "Digital tools and workflows designed to help healthcare professionals deliver connected care.",
    },
    {
      icon: Video,
      title: "Virtual Care",
      description:
        "Connect patients and healthcare professionals through supported virtual consultation experiences.",
    },
    {
      icon: FileText,
      title: "Digital Health Records",
      description:
        "Keep relevant patient information organised through connected digital record workflows.",
    },
    {
      icon: Pill,
      title: "e-Prescriptions",
      description:
        "Support digital prescription workflows as part of a connected healthcare journey.",
    },
    {
      icon: CalendarCheck,
      title: "Healthcare Scheduling",
      description:
        "Manage appointments, availability and scheduling through organised digital workflows.",
    },
    {
      icon: Users,
      title: "Patient Experience",
      description:
        "Create simpler ways for patients to discover and engage with healthcare services.",
    },
  ];

  const values = [
    {
      icon: HeartPulse,
      title: "Patient First",
      description:
        "Healthcare experiences should be designed around patients and the professionals who care for them.",
    },
    {
      icon: Lightbulb,
      title: "Meaningful Innovation",
      description:
        "We focus on technology that solves real healthcare problems without unnecessary complexity.",
    },
    {
      icon: ShieldCheck,
      title: "Trust & Privacy",
      description:
        "Privacy, security and responsible handling of information are fundamental to healthcare.",
    },
    {
      icon: Users,
      title: "Connected Care",
      description:
        "Better healthcare experiences happen when patients, professionals and workflows work together.",
    },
  ];

  const journey = [
    {
      number: "01",
      title: "Discover",
      description:
        "Help patients find healthcare services and professionals relevant to their needs.",
    },
    {
      number: "02",
      title: "Connect",
      description:
        "Create meaningful connections between patients and healthcare professionals.",
    },
    {
      number: "03",
      title: "Care",
      description:
        "Support consultations, records, prescriptions and other healthcare workflows.",
    },
    {
      number: "04",
      title: "Continue",
      description:
        "Keep the healthcare journey connected beyond a single appointment.",
    },
  ];

  const platformAreas = [
    {
      icon: Activity,
      title: "Connected Healthcare",
      description:
        "Bring different healthcare interactions together through one connected digital ecosystem.",
    },
    {
      icon: Stethoscope,
      title: "Care Delivery",
      description:
        "Support patients and healthcare professionals across virtual and in-person care experiences.",
    },
    {
      icon: ClipboardCheck,
      title: "Clinical Workflows",
      description:
        "Connect appointments, health records, prescriptions, diagnostics and other workflows.",
    },
    {
      icon: ShieldCheck,
      title: "Secure Experiences",
      description:
        "Build healthcare experiences around privacy, security and responsible information handling.",
    },
  ];

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =========================================================
            HERO
        ========================================================= */}
        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-teal-200/25 blur-3xl" />

          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-200/25 blur-3xl" />

          <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32 grid lg:grid-cols-2 gap-16 items-center">

            {/* HERO CONTENT */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  About Doxzon
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[64px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Healthcare,
                <span className="block text-teal-500 mt-2">
                  better connected.
                </span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-7 max-w-xl">
                Doxzon is a connected digital healthcare platform from
                Cytozon Technologies, bringing patients, healthcare
                professionals and healthcare services together through
                connected experiences.
              </p>

              <div className="flex flex-wrap gap-3 mt-9">

                <Link
                  to="/"
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-6 h-12 text-sm font-semibold hover:bg-slate-800 transition"
                >
                  Explore Doxzon
                  <ArrowRight size={16} />
                </Link>

                <a
                  href="#vision"
                  className="inline-flex items-center gap-2 rounded-xl bg-white border border-slate-200 text-slate-800 px-6 h-12 text-sm font-semibold hover:border-teal-200 hover:bg-teal-50 transition"
                >
                  Our Vision
                </a>

              </div>

              {/* COMPANY RELATIONSHIP */}
              <div className="flex items-center gap-3 mt-7 text-sm text-slate-500">

                <div className="w-8 h-8 rounded-lg bg-white border border-slate-100 grid place-items-center">
                  <Building2 size={15} className="text-teal-600" />
                </div>

                <span>
                  A healthcare technology product from{" "}
                  <span className="font-semibold text-slate-800">
                    Cytozon Technologies
                  </span>
                </span>

              </div>

            </motion.div>

            {/* HERO VISUAL */}
            <motion.div
              initial={{ opacity: 0, x: 30, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >

              <div className="absolute -inset-8 rounded-[45px] bg-teal-200/30 blur-3xl" />

              <div className="relative bg-white rounded-[32px] border border-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.14)] p-7">

                <div className="flex items-center justify-between">

                  <div>

                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Doxzon Platform
                    </p>

                    <h3 className="font-sora text-xl font-semibold mt-1">
                      Connected Healthcare
                    </h3>

                  </div>

                  <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center">
                    <HeartPulse
                      size={24}
                      className="text-teal-600"
                    />
                  </div>

                </div>

                <div className="relative flex justify-center my-10">

                  <div className="absolute w-56 h-56 rounded-full border border-teal-100" />

                  <div className="absolute w-40 h-40 rounded-full border border-teal-100" />

                  <div className="relative z-10 w-24 h-24 rounded-[28px] bg-slate-950 grid place-items-center shadow-xl">

                    <HeartPulse
                      size={38}
                      className="text-teal-400"
                    />

                  </div>

                </div>

                <div className="grid grid-cols-2 gap-3">

                  {[
                    [Users, "Patients"],
                    [Stethoscope, "Professionals"],
                    [Video, "Virtual Care"],
                    [FileText, "Digital Records"],
                  ].map(([Icon, title]) => (

                    <div
                      key={title}
                      className="rounded-2xl bg-slate-50 border border-slate-100 p-4"
                    >

                      <div className="w-9 h-9 rounded-xl bg-white grid place-items-center">

                        <Icon
                          size={17}
                          className="text-teal-600"
                        />

                      </div>

                      <p className="text-xs font-semibold mt-3">
                        {title}
                      </p>

                    </div>

                  ))}

                </div>

              </div>

            </motion.div>

          </div>
        </section>


        {/* =========================================================
            ABOUT CYTOZON + DOXZON
        ========================================================= */}
        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6">

            <div className="max-w-3xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Built by Cytozon
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Technology expertise,
                <span className="block text-teal-500">
                  focused on healthcare.
                </span>

              </h2>

              <p className="text-slate-600 leading-8 mt-6">
                Cytozon Technologies is a technology and software engineering
                company focused on building modern digital solutions.
                Doxzon represents its healthcare-focused platform, bringing
                that technology expertise into connected healthcare
                experiences.
              </p>

              <p className="text-slate-600 leading-8 mt-4">
                Doxzon is designed to connect patients, healthcare
                professionals, diagnostics, medicines, records and other
                healthcare workflows through a single digital ecosystem.
              </p>

            </div>

            <div className="grid md:grid-cols-3 gap-4 mt-12">

              <div className="rounded-2xl bg-slate-950 text-white p-6">

                <Building2
                  size={22}
                  className="text-teal-400"
                />

                <h3 className="font-sora text-lg font-semibold mt-5">
                  Cytozon
                </h3>

                <p className="text-slate-400 text-sm leading-6 mt-2">
                  Technology and software engineering expertise.
                </p>

              </div>

              <div className="rounded-2xl bg-teal-500 text-white p-6">

                <HeartPulse size={22} />

                <h3 className="font-sora text-lg font-semibold mt-5">
                  Doxzon
                </h3>

                <p className="text-teal-50 text-sm leading-6 mt-2">
                  A connected digital healthcare platform.
                </p>

              </div>

              <div className="rounded-2xl bg-slate-50 border border-slate-100 p-6">

                <Sparkles
                  size={22}
                  className="text-teal-600"
                />

                <h3 className="font-sora text-lg font-semibold mt-5">
                  Healthcare
                </h3>

                <p className="text-slate-500 text-sm leading-6 mt-2">
                  Technology designed around real healthcare journeys.
                </p>

              </div>

            </div>

          </div>

        </section>


        {/* =========================================================
            VISION
        ========================================================= */}
        <section
          id="vision"
          className="py-24 md:py-28 bg-slate-950 text-white"
        >

          <div className="max-w-5xl mx-auto px-6 text-center">

            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
              Our Vision
            </span>

            <h2 className="font-sora text-4xl md:text-6xl font-medium mt-5">

              A healthcare ecosystem where
              <span className="text-teal-400">
                {" "}everything connects.
              </span>

            </h2>

            <p className="text-slate-400 text-lg leading-8 max-w-3xl mx-auto mt-7">
              We believe digital healthcare should not feel like a collection
              of disconnected tools. Patients, professionals, records,
              appointments and services should work together as part of a
              simpler and more connected journey.
            </p>

          </div>

        </section>


        {/* =========================================================
            WHAT DOXZON DOES
        ========================================================= */}
        <section className="py-24 md:py-28">

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                What Doxzon Does
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                One platform,
                <span className="text-teal-500">
                  {" "}multiple healthcare workflows.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Doxzon brings together healthcare services and digital
                workflows designed to support both patients and healthcare
                professionals.
              </p>

            </div>

            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">

              {services.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.06 }}
                    whileHover={{ y: -5 }}
                    className="group rounded-2xl border border-slate-100 bg-slate-50/70 p-5 hover:bg-white hover:shadow-lg hover:border-teal-100 transition"
                  >

                    <div className="w-10 h-10 rounded-xl bg-teal-50 grid place-items-center group-hover:bg-teal-500 transition">

                      <Icon
                        size={18}
                        className="text-teal-600 group-hover:text-white transition"
                      />

                    </div>

                    <h3 className="font-sora text-base font-semibold mt-4">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-xs leading-6 mt-2">
                      {item.description}
                    </p>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>


        {/* =========================================================
            PLATFORM AREAS
        ========================================================= */}
        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                The Doxzon Platform
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Designed around
                <span className="text-teal-500">
                  {" "}the complete care journey.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                From finding care to continuing care, Doxzon brings the
                important parts of the healthcare journey closer together.
              </p>

            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-12">

              {platformAreas.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.07 }}
                    whileHover={{ y: -4 }}
                    className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm hover:shadow-md transition"
                  >

                    <div className="flex items-start gap-4">

                      <div className="w-10 h-10 shrink-0 rounded-xl bg-teal-50 grid place-items-center">

                        <Icon
                          size={18}
                          className="text-teal-600"
                        />

                      </div>

                      <div>

                        <h3 className="font-sora text-base font-semibold">
                          {item.title}
                        </h3>

                        <p className="text-slate-500 text-xs leading-6 mt-1.5">
                          {item.description}
                        </p>

                      </div>

                    </div>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>


        {/* =========================================================
            JOURNEY
        ========================================================= */}
        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                The Doxzon Journey
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                From discovery to
                <span className="text-teal-500">
                  {" "}ongoing care.
                </span>

              </h2>

            </div>

            <div className="relative grid md:grid-cols-4 gap-6 mt-14">

              <div className="hidden md:block absolute top-7 left-[11%] right-[11%] h-px bg-teal-100" />

              {journey.map((item) => (

                <motion.div
                  key={item.number}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="relative text-center"
                >

                  <div className="relative z-10 mx-auto w-14 h-14 rounded-2xl bg-slate-950 border-4 border-white shadow-lg grid place-items-center">

                    <span className="text-teal-400 text-xs font-bold">
                      {item.number}
                    </span>

                  </div>

                  <h3 className="font-sora text-lg font-semibold mt-6">
                    {item.title}
                  </h3>

                  <p className="text-slate-500 text-sm leading-6 mt-2 max-w-[230px] mx-auto">
                    {item.description}
                  </p>

                </motion.div>

              ))}

            </div>

          </div>

        </section>


        {/* =========================================================
            VALUES
        ========================================================= */}
        <section className="py-24 md:py-28 bg-slate-50">

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Our Values
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                What guides
                <span className="text-teal-500">
                  {" "}Doxzon.
                </span>

              </h2>

            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">

              {values.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.07 }}
                    whileHover={{ y: -4 }}
                    className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm hover:shadow-md hover:border-teal-100 transition"
                  >

                    <div className="w-10 h-10 rounded-xl bg-teal-50 grid place-items-center">

                      <Icon
                        size={18}
                        className="text-teal-600"
                      />

                    </div>

                    <h3 className="font-sora text-base font-semibold mt-4">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-xs leading-6 mt-2">
                      {item.description}
                    </p>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>


        {/* =========================================================
            WHY DOXZON
        ========================================================= */}
        <section className="py-24 md:py-28 bg-gradient-to-br from-cyan-50 to-blue-50">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Why Doxzon
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Technology built around the
                <span className="block text-teal-500">
                  complete care journey.
                </span>

              </h2>

              <p className="text-slate-600 leading-8 mt-6">
                Healthcare is more than a single appointment. Doxzon is
                designed around the different interactions that can happen
                before, during and after care.
              </p>

              <p className="text-slate-600 leading-8 mt-4">
                By bringing healthcare workflows closer together, Doxzon aims
                to create simpler digital experiences for patients and
                healthcare professionals.
              </p>

            </div>

            <div className="space-y-3">

              {[
                "Connected healthcare ecosystem",
                "Patient-focused digital experiences",
                "Tools designed for healthcare professionals",
                "Connected clinical workflows",
                "Support for physical and virtual care",
                "Technology built around trust",
              ].map((item) => (

                <div
                  key={item}
                  className="flex items-center gap-4 rounded-2xl border border-white bg-white/80 p-4 shadow-sm"
                >

                  <span className="w-8 h-8 shrink-0 rounded-full bg-teal-50 text-teal-600 grid place-items-center">

                    <Check size={15} />

                  </span>

                  <span className="text-sm md:text-base font-medium text-slate-700">
                    {item}
                  </span>

                </div>

              ))}

            </div>

          </div>

        </section>


        {/* =========================================================
            CYTOZON CONNECTION
        ========================================================= */}
        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6">

            <div className="rounded-[32px] bg-slate-950 text-white p-8 md:p-12">

              <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">

                <div>

                  <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                    From Cytozon Technologies
                  </span>

                  <h2 className="font-sora text-3xl md:text-4xl font-medium mt-4">

                    Technology expertise,
                    <span className="text-teal-400">
                      {" "}healthcare purpose.
                    </span>

                  </h2>

                  <p className="text-slate-300 leading-7 mt-5">
                    Doxzon brings Cytozon's technology and engineering
                    capabilities into a healthcare-focused product designed
                    around connected digital care.
                  </p>

                  <a
                    href="https://www.cytozon.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-7 inline-flex items-center gap-2 rounded-xl bg-white text-slate-900 px-6 h-11 text-sm font-semibold hover:bg-teal-50 transition"
                  >
                    Visit Cytozon
                    <ArrowRight size={15} />
                  </a>

                </div>

                <div className="grid grid-cols-2 gap-3">

                  <div className="rounded-2xl bg-white/5 border border-white/10 p-5">

                    <Building2
                      size={20}
                      className="text-teal-400"
                    />

                    <p className="text-sm font-semibold mt-4">
                      Technology
                    </p>

                    <p className="text-xs text-slate-400 leading-5 mt-1">
                      Software engineering and digital transformation.
                    </p>

                  </div>

                  <div className="rounded-2xl bg-white/5 border border-white/10 p-5">

                    <HeartPulse
                      size={20}
                      className="text-teal-400"
                    />

                    <p className="text-sm font-semibold mt-4">
                      Healthcare
                    </p>

                    <p className="text-xs text-slate-400 leading-5 mt-1">
                      Connected digital healthcare experiences.
                    </p>

                  </div>

                  <div className="rounded-2xl bg-white/5 border border-white/10 p-5">

                    <ShieldCheck
                      size={20}
                      className="text-teal-400"
                    />

                    <p className="text-sm font-semibold mt-4">
                      Trust
                    </p>

                    <p className="text-xs text-slate-400 leading-5 mt-1">
                      Security and responsible healthcare technology.
                    </p>

                  </div>

                  <div className="rounded-2xl bg-white/5 border border-white/10 p-5">

                    <Sparkles
                      size={20}
                      className="text-teal-400"
                    />

                    <p className="text-sm font-semibold mt-4">
                      Innovation
                    </p>

                    <p className="text-xs text-slate-400 leading-5 mt-1">
                      Technology focused on meaningful healthcare needs.
                    </p>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =========================================================
            CTA
        ========================================================= */}
        <section className="px-6 pb-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-slate-950 text-white px-8 py-16 md:px-16 text-center">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-500/15 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />

            <div className="relative">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Connected Healthcare
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Discover the Doxzon
                <span className="text-teal-400">
                  {" "}healthcare ecosystem.
                </span>

              </h2>

              <p className="text-slate-300 max-w-2xl mx-auto mt-5 leading-7">
                Explore connected digital healthcare experiences designed
                for patients and healthcare professionals.
              </p>

              <Link
                to="/"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-7 py-4 font-semibold hover:bg-teal-50 transition"
              >
                Explore Doxzon
                <ArrowRight size={17} />
              </Link>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default AboutDoxzon;